import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../../utils/cn';

interface FieldShellProps {
  id: string;
  label: string;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  required?: boolean | undefined;
  children: ReactNode;
}

/** Label + control + hint/error, with aria wiring (WCAG 2.2 AA, docs/design/15 §5). */
export function FieldShell({ id, label, hint, error, required, children }: FieldShellProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-fg">
        {label}
        {required ? (
          <span className="ml-0.5 text-danger" aria-hidden>
            *
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const controlClass = (error?: string) =>
  cn(
    'w-full rounded-md border bg-surface px-3 text-sm text-fg placeholder:text-fg-subtle disabled:opacity-60',
    error ? 'border-danger' : 'border-line',
  );

function describedBy(id: string, error?: string, hint?: ReactNode) {
  return error ? `${id}-error` : hint ? `${id}-hint` : undefined;
}

type BaseProps = { label: string; hint?: ReactNode; error?: string | undefined };

export const TextField = forwardRef<HTMLInputElement, BaseProps & InputHTMLAttributes<HTMLInputElement>>(
  function TextField({ label, hint, error, id, className, required, ...rest }, ref) {
    const generated = useId();
    const inputId = id ?? generated;
    return (
      <FieldShell id={inputId} label={label} hint={hint} error={error} required={required}>
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(inputId, error, hint)}
          className={cn(controlClass(error), 'h-9', className)}
          required={required}
          {...rest}
        />
      </FieldShell>
    );
  },
);

export const TextAreaField = forwardRef<
  HTMLTextAreaElement,
  BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement>
>(function TextAreaField({ label, hint, error, id, className, required, ...rest }, ref) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <FieldShell id={inputId} label={label} hint={hint} error={error} required={required}>
      <textarea
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(inputId, error, hint)}
        className={cn(controlClass(error), 'min-h-20 py-2', className)}
        required={required}
        {...rest}
      />
    </FieldShell>
  );
});

export const SelectField = forwardRef<
  HTMLSelectElement,
  BaseProps &
    SelectHTMLAttributes<HTMLSelectElement> & {
      options: { value: string; label: string }[];
      placeholder?: string;
    }
>(function SelectField({ label, hint, error, id, className, options, placeholder, required, ...rest }, ref) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <FieldShell id={inputId} label={label} hint={hint} error={error} required={required}>
      <select
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(inputId, error, hint)}
        className={cn(controlClass(error), 'h-9', className)}
        required={required}
        {...rest}
      >
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
});

export const CheckboxField = forwardRef<
  HTMLInputElement,
  { label: string; description?: string } & InputHTMLAttributes<HTMLInputElement>
>(function CheckboxField({ label, description, id, ...rest }, ref) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <div className="flex items-start gap-3">
      <input ref={ref} id={inputId} type="checkbox" className="mt-0.5 size-4 accent-brand-600" {...rest} />
      <label htmlFor={inputId} className="text-sm">
        <span className="font-medium text-fg">{label}</span>
        {description ? <span className="block text-fg-muted">{description}</span> : null}
      </label>
    </div>
  );
});

/** Puts server-side field errors from an ApiError onto a React Hook Form. */
export function applyServerErrors(
  fieldErrors: Record<string, string>,
  setError: (name: never, error: { type: string; message: string }) => void,
): string[] {
  const unmatched: string[] = [];
  for (const [path, message] of Object.entries(fieldErrors)) {
    if (path) setError(path as never, { type: 'server', message });
    else unmatched.push(message);
  }
  return unmatched;
}
